import { find, get, uniq } from "lodash";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { GENERATE_SUBSCRIPTION_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { UPDATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import { KEYS } from "utils/constants";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

const SelectCombination = ({
  project,
  onNext,
  onBack,
  currentStatus,
  tracer,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [selectedCombinationId, setSelectedCombinationId] = useState(
    get(project, "fields.selectedCombinationId"),
  );

  const combinations = useMemo(
    () => get(project, "fields.combinations", []),
    [project],
  );

  const upsellsByBase = useMemo(
    () => get(project, "fields.upsells", {}),
    [project],
  );

  const baseInsurancePlanIds = uniq(
    combinations.map(({ baseInsurancePlanId }) => baseInsurancePlanId),
  );

  const { data, loading } = useQuery(GENERAL_COSTS, {
    skip: baseInsurancePlanIds.length === 0,
    variables: {
      where: {
        project: { id: project.id },
      },
      isIn: {
        insurancePlan: { id: baseInsurancePlanIds },
      },
    },
  });

  const baseGeneralCosts = get(data, "generalCosts.data", []);

  const enrichedCombinations = useMemo(() => {
    return combinations.map((combination) => {
      const baseGeneralCost = find(
        baseGeneralCosts,
        (generalCost) =>
          get(generalCost, "insurancePlan.id") ===
          combination.baseInsurancePlanId,
      );
      const baseInsurancePlan = get(baseGeneralCost, "insurancePlan");
      const baseCost = get(baseGeneralCost, "cost", 0);
      const currency = get(baseGeneralCost, "currency");
      const complementModule = combination.complementaryInsurancePlanId
        ? find(
            get(upsellsByBase, combination.baseInsurancePlanId, []),
            (module) =>
              get(module, "insurancePlan.id") ===
              combination.complementaryInsurancePlanId,
          )
        : null;
      const complementaryInsurancePlan = get(complementModule, "insurancePlan");
      const complementCost = get(complementModule, "cost", 0);
      const baseName = get(baseInsurancePlan, "name", "");
      const complementName = get(complementaryInsurancePlan, "name");
      return {
        id: combination.id,
        baseInsurancePlanId: combination.baseInsurancePlanId,
        complementaryInsurancePlanId:
          combination.complementaryInsurancePlanId || null,
        baseInsurancePlan,
        complementaryInsurancePlan,
        baseCost,
        complementCost,
        totalCost: baseCost + complementCost,
        currency,
        label: complementName ? `${baseName} avec ${complementName}` : baseName,
      };
    });
  }, [combinations, baseGeneralCosts, upsellsByBase]);

  const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION);
  const [generateSubscriptionDocuments] = useMutation(
    GENERATE_SUBSCRIPTION_DOCUMENTS,
  );

  const onSubmit = () => {
    const combination = find(
      enrichedCombinations,
      ({ id }) => id === selectedCombinationId,
    );
    if (!combination) return;
    const subscriptionId =
      localStorage.getItem(KEYS.SUBSCRIPTION_ID) ||
      get(project, "fields.currentSubscription");
    if (!subscriptionId) return;
    setSubmitting(true);
    updateSubscription({
      variables: {
        where: { id: subscriptionId },
        data: {
          insurancePlan: { id: combination.baseInsurancePlanId },
          additionalInfo: {
            combinationId: combination.id,
            complementaryInsurancePlanId:
              combination.complementaryInsurancePlanId || null,
          },
        },
      },
      onCompleted: () => {
        localStorage.setItem(KEYS.SUBSCRIPTION_ID, subscriptionId);
        generateSubscriptionDocuments({
          variables: {
            data: { id: subscriptionId },
          },
          onCompleted: () => {
            onNext({
              payload: {
                fields: {
                  ...get(project, "fields", {}),
                  currentSubscription: subscriptionId,
                  selectedCombinationId: combination.id,
                },
              },
              onCompleted: () => setSubmitting(false),
            });
          },
          onError: () => setSubmitting(false),
        });
      },
      onError: () => setSubmitting(false),
    });
  };

  if (loading) return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
        showCart
        step={4}
        stepCount={5}
        title="Mes informations contractuelles"
      />
      <View
        combinations={enrichedCombinations}
        selectedCombinationId={selectedCombinationId}
        onSelect={setSelectedCombinationId}
        onNext={onSubmit}
        onBack={onBack}
        loading={submitting}
      />
    </>
  );
};

export default SelectCombination;
